import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { startExport } from '../audit-api'
import { auditErrorText } from '../entry-format'
import { customParams, MAX_RANGE_DAYS, pickerDay, RANGES as PAGE_RANGES } from '../filters-model'
import './audit-log.scss'
import { ErrorReference } from '../../error/error-reference'
import { useErrorLine } from '../../error/use-error-line'

const DAY = 864e5
const RANGES = [
  { value: '30d', label: 'Last 30 days', ms: 30 * DAY },
  { value: '90d', label: 'Last 90 days', ms: 90 * DAY },
  { value: '365d', label: 'Last 12 months', ms: 365 * DAY },
  { value: 'custom', label: 'Custom…' }
]

// The dialog starts from the page's own date range, so "Uses the filters
// you've applied" holds: a shorter page range (24 hours, 7 days) is offered
// as well; a custom one carries its days.
const rangesFor = page => {
  const extra = PAGE_RANGES.find(r => r.ms && r.value === page && !RANGES.some(x => x.value === page))
  return extra ? [{ value: extra.value, label: extra.label, ms: extra.ms }, ...RANGES] : RANGES
}
const startFrom = page => {
  const range = page && page.range
  if (range === 'custom') return { range: 'custom', from: page.from || '', to: page.to || '' }
  if (range && rangesFor(range).some(r => r.value === range)) return { range, from: '', to: '' }
  return { range: '30d', from: '', to: '' }
}
const IN_PROGRESS = 'You already have an export running. Wait for it to finish, then try again.'
const TOO_LONG = `Choose a range of ${MAX_RANGE_DAYS} days or less.`

// The window an export covers: one of the ranges ending now, or whole local
// days (from 00:00 on the first to the end of the last), or -- for a page
// range an Insights pivot set -- its exact UTC instants, unchanged.
const windowOf = ({ range, from, to }, now) => {
  const options = rangesFor(range)
  if (range === 'custom') {
    return customParams({ range, from, to }, now, { clamp: false })
  }
  const r = options.find(x => x.value === range) || RANGES[0]
  return { from: new Date(now.getTime() - r.ms).toISOString(), to: now.toISOString() }
}

const tooLong = (choice, now) => {
  if (choice.range !== 'custom' || !choice.from) return false
  const w = windowOf(choice, now)
  return new Date(w.to) - new Date(w.from) > MAX_RANGE_DAYS * DAY
}

// The page's applied filters as the export worker reads them: every value a
// string (content parses params as map[string]string), lists joined with
// commas, empty values and paging (before, limit) dropped, and the window
// replaced by the one chosen here.
export const exportParams = (params, choice, now = new Date()) => {
  const out = {}
  Object.entries(params || {}).forEach(([k, v]) => {
    if (['before', 'limit', 'from', 'to'].includes(k)) return
    if (v === undefined || v === null || v === '') return
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',')
      return
    }
    out[k] = String(v)
  })
  return { ...out, ...windowOf(choice, now) }
}

const FOCUSABLE = 'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

// "Export…" on the Audit log (mockup "export modal"; ConfirmDialog's modal
// pattern: portalled, Escape cancels). Focus moves into the dialog when it
// opens, Tab stays inside it, and focus returns to what opened it on close.
// The server allows one export at a time per person: its 409 is shown as a
// plain sentence. onStarted(id) hands the new export to Recent exports; the
// download link is never read here. range: the page's filters ({ range,
// from, to }), which the date range starts from.
export const ExportDialog = ({ open, params, range, onClose, onStarted }) => {
  const [choice, setChoice] = useState(() => startFrom(range))
  const box = useRef(null)
  const [format, setFormat] = useState('csv')
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  useEffect(() => {
    if (!open) return undefined
    setError(null)
    setBusy(false)
    const opener = document.activeElement
    const first = box.current && box.current.querySelector(FOCUSABLE)
    if (first) first.focus()
    const onKey = e => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !box.current) return
      const items = [...box.current.querySelectorAll(FOCUSABLE)]
      if (!items.length) return
      const head = items[0]
      const tail = items[items.length - 1]
      const inside = box.current.contains(document.activeElement)
      if (e.shiftKey && (document.activeElement === head || !inside)) {
        e.preventDefault()
        tail.focus()
      } else if (!e.shiftKey && (document.activeElement === tail || !inside)) {
        e.preventDefault()
        head.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus()
    }
  }, [open, onClose, setError])

  // Each opening starts again from the page's range.
  const rangeKey = range ? `${range.range}|${range.from}|${range.to}` : ''
  useEffect(() => {
    if (open) setChoice(startFrom(range))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rangeKey])

  if (!open) return null

  const set = patch => {
    setError(null)
    setChoice(c => ({ ...c, ...patch }))
  }

  const submit = async () => {
    const now = new Date()
    if (tooLong(choice, now)) {
      setError(TOO_LONG)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const started = await startExport(format, exportParams(params, choice, now))
      onStarted(started && started.id)
    } catch (err) {
      setError(err && err.status === 409 ? auditErrorText(err, IN_PROGRESS) : auditErrorText(err, "The export couldn't be started."), err)
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sw-dialog au-export" ref={box} role="dialog" aria-modal="true" aria-labelledby="au-exp-title">
        <h2 className="h5" id="au-exp-title">Export the audit log</h2>
        <p className="sw-hint m-0">Uses the filters you've applied. Large exports are prepared in the background and appear under Recent exports for 7 days.</p>

        <div className="au-field">
          <label htmlFor="au-exp-range">Date range</label>
          <select id="au-exp-range" className="form-select form-select-sm" value={choice.range} onChange={e => set({ range: e.target.value })}>
            {rangesFor(range && range.range).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {choice.range === 'custom' &&
            <div className="au-exp-dates">
              <label className="sw-hint small">From<input type="date" className="form-control form-control-sm" value={pickerDay(choice.from)} max={pickerDay(choice.to, true) || undefined} onChange={e => set({ from: e.target.value })} /></label>
              <label className="sw-hint small">To<input type="date" className="form-control form-control-sm" value={pickerDay(choice.to, true)} min={pickerDay(choice.from) || undefined} onChange={e => set({ to: e.target.value })} /></label>
            </div>}
        </div>

        <fieldset className="au-field">
          <legend>Format</legend>
          <div className="au-radios">
            <label><input type="radio" name="au-exp-format" value="csv" checked={format === 'csv'} onChange={() => setFormat('csv')} />CSV for spreadsheets</label>
            <label><input type="radio" name="au-exp-format" value="ndjson" checked={format === 'ndjson'} onChange={() => setFormat('ndjson')} />JSON lines (for security tools)</label>
          </div>
        </fieldset>

        <p className="sw-hint small m-0">This export is recorded in the audit log.</p>
        {error && <p className="text-danger small m-0" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mt-0" />}

        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn sw-btn-primary" disabled={busy} onClick={submit}>Export</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
